import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import api, { classifyApiError } from '../utils/api'
import { useMobileAuth } from '../contexts/MobileAuthContext'
import IntegrationEventCard from '../components/IntegrationEventCard'
import ErrorState from '../components/ErrorState'
import FranchiseUsageStrip from '../components/FranchiseUsageStrip'
import AutoRefreshTimer from '../components/AutoRefreshTimer'
import logo from '../assets/logo.png'
import { PERIOD_OPTIONS, getPeriodDateRange, getPeriodDayKeys } from '../utils/statsPeriod'
import { LAYOUT_MODES, useLayoutMode } from '../utils/useLayoutMode'
import { CHART_TYPES, useChartType } from '../utils/useChartType'

const PERIOD_STORAGE_KEY = 'mobile_dashboard_period'

const PERIOD_EMPTY_LABEL = {
  today: 'hoje',
  yesterday: 'ontem',
  '7d': 'nos últimos 7 dias',
  '15d': 'nos últimos 15 dias',
  '30d': 'nos últimos 30 dias'
}

const PeriodToggle = ({ period, onPeriodChange }) => (
  <div className="inline-flex items-center rounded-full border border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-800 p-0.5">
    {PERIOD_OPTIONS.map((opt) => (
      <React.Fragment key={opt.value}>
        {opt.value === '7d' && <span className="w-px h-4 mx-0.5 bg-gray-200 dark:bg-gray-700" />}
        <button
          type="button"
          onClick={() => onPeriodChange(opt.value)}
          className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition active:scale-95 ${
            period === opt.value
              ? 'bg-white dark:bg-gray-700 text-blue-700 dark:text-blue-300 shadow-sm'
              : 'text-gray-500 dark:text-gray-400'
          }`}
        >
          {opt.label}
        </button>
      </React.Fragment>
    ))}
  </div>
)

const LAYOUT_MODE_OPTIONS = [
  { value: LAYOUT_MODES.MOBILE, icon: 'smartphone', label: '1 coluna' },
  { value: LAYOUT_MODES.MONITOR, icon: 'desktop_windows', label: 'Monitor (auto-ajuste)' }
]

// Grid classes per mode - mobile stays a plain stacked column (unchanged
// default behavior), monitor lets the grid fit as many ~260px columns as
// the available width allows.
const CARDS_CONTAINER_CLASS = {
  [LAYOUT_MODES.MOBILE]: 'flex flex-col gap-4',
  [LAYOUT_MODES.MONITOR]: 'grid gap-4 grid-cols-[repeat(auto-fit,minmax(260px,1fr))]'
}

const LayoutModeToggle = ({ layoutMode, onLayoutModeChange }) => (
  <div className="inline-flex items-center rounded-full border border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-800 p-0.5">
    {LAYOUT_MODE_OPTIONS.map((opt) => (
      <button
        key={opt.value}
        type="button"
        onClick={() => onLayoutModeChange(opt.value)}
        title={opt.label}
        className={`w-7 h-7 flex items-center justify-center rounded-full transition active:scale-95 ${
          layoutMode === opt.value
            ? 'bg-white dark:bg-gray-700 text-blue-700 dark:text-blue-300 shadow-sm'
            : 'text-gray-500 dark:text-gray-400'
        }`}
      >
        <span className="material-icons" style={{ fontSize: '15px' }}>{opt.icon}</span>
      </button>
    ))}
  </div>
)

const CHART_TYPE_OPTIONS = [
  { value: CHART_TYPES.PIE, icon: 'pie_chart', label: 'Gráfico de pizza' },
  { value: CHART_TYPES.LINE, icon: 'show_chart', label: 'Gráfico de linhas' },
  { value: CHART_TYPES.BAR, icon: 'bar_chart', label: 'Gráfico de barras' }
]

const ChartTypeToggle = ({ chartType, onChartTypeChange }) => (
  <div className="inline-flex items-center rounded-full border border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-800 p-0.5">
    {CHART_TYPE_OPTIONS.map((opt) => (
      <button
        key={opt.value}
        type="button"
        onClick={() => onChartTypeChange(opt.value)}
        title={opt.label}
        className={`w-7 h-7 flex items-center justify-center rounded-full transition active:scale-95 ${
          chartType === opt.value
            ? 'bg-white dark:bg-gray-700 text-blue-700 dark:text-blue-300 shadow-sm'
            : 'text-gray-500 dark:text-gray-400'
        }`}
      >
        <span className="material-icons" style={{ fontSize: '15px' }}>{opt.icon}</span>
      </button>
    ))}
  </div>
)

const CardSkeleton = () => (
  <div className="bg-white/80 dark:bg-gray-800/70 backdrop-blur-sm rounded-2xl border border-white/60 dark:border-white/10 p-4 animate-pulse">
    <div className="flex items-start gap-2.5">
      <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700 flex-shrink-0" />
      <div className="flex-1">
        <div className="h-3.5 w-28 bg-gray-200 dark:bg-gray-700 rounded mb-2" />
        <div className="h-2.5 w-20 bg-gray-200 dark:bg-gray-700 rounded" />
      </div>
    </div>
    <div className="h-[100px] mt-3 bg-gray-100 dark:bg-gray-700/50 rounded-lg" />
  </div>
)

const DashboardPage = ({ isDarkMode, toggleDarkMode }) => {
  const { companyId, username, companyName, token, logout } = useMobileAuth()
  const navigate = useNavigate()
  const [stats, setStats] = useState([])
  const [loading, setLoading] = useState(true)
  const [errorType, setErrorType] = useState(null)
  // Kept in sessionStorage so coming back from IncidencesPage (which unmounts
  // this page) restores the period the user had picked.
  const [period, setPeriodState] = useState(() => {
    try {
      const saved = sessionStorage.getItem(PERIOD_STORAGE_KEY)
      return PERIOD_OPTIONS.some((opt) => opt.value === saved) ? saved : 'today'
    } catch {
      return 'today'
    }
  })
  const setPeriod = (value) => {
    setPeriodState(value)
    try { sessionStorage.setItem(PERIOD_STORAGE_KEY, value) } catch { /* storage unavailable */ }
  }
  const [usageOpen, setUsageOpen] = useState(false)
  const { layoutMode, setLayoutMode } = useLayoutMode()
  const cardsContainerClass = CARDS_CONTAINER_CLASS[layoutMode] || CARDS_CONTAINER_CLASS[LAYOUT_MODES.MOBILE]
  const { chartType, setChartType } = useChartType()

  const loadStats = useCallback(async () => {
    if (!companyId) return
    setLoading(true)
    setErrorType(null)
    try {
      const { dateFrom, dateTo } = getPeriodDateRange(period)
      const response = await api.get(`/api/cloud-event-logs/company/${companyId}/stats`, {
        params: dateFrom && dateTo ? { date_from: dateFrom, date_to: dateTo } : undefined
      })
      const statsData = response.data?.integrations || []
      setStats(Array.isArray(statsData) ? statsData : [])
    } catch (error) {
      setErrorType(classifyApiError(error))
    } finally {
      setLoading(false)
    }
  }, [companyId, period])

  useEffect(() => {
    loadStats()
  }, [loadStats])

  const handleLogout = () => {
    logout()
    navigate('/login', { replace: true })
  }

  const handleOpenIncidences = (stat) => {
    navigate(`/incidencias/${stat.integration_id}?period=${period}`, { state: { stat } })
  }

  const totalEvents =stats.reduce((sum, s) => sum + (s.event_count || 0), 0)
  const totalProcessing = stats.reduce((sum, s) => sum + (s.processing || 0), 0)

  // Bucketed the same way each IntegrationEventCard computes its own totals
  // (sum of `daily` entries falling within the selected period's day keys),
  // so the top summary always matches what the cards below show.
  const periodDayKeys = new Set(getPeriodDayKeys(period))
  const sumDaily = (field) => stats.reduce((sum, s) => (
    sum + (s.daily || []).reduce((acc, d) => acc + (periodDayKeys.has(d.date) ? (d[field] || 0) : 0), 0)
  ), 0)
  const totalSuccess = sumDaily('success')
  const totalErrors = sumDaily('error')
  const totalFinished = totalSuccess + totalErrors
  const successRate = totalFinished > 0 ? (totalSuccess / totalFinished) * 100 : 0

  return (
    <div className="h-full flex flex-col bg-gray-50 dark:bg-gray-950">
      {/* Header */}
      <div className="flex-shrink-0 flex items-center justify-between gap-3 px-4 py-3 safe-top bg-white/90 dark:bg-gray-900/90 backdrop-blur-md border-b border-gray-200 dark:border-gray-800">
          <div className="flex items-start gap-2.5 min-w-0">
            <div className="w-9 h-9 rounded-full overflow-hidden flex-shrink-0 ring-2 ring-white/50 dark:ring-white/10">
              <img src={logo} alt="SF" className="w-full h-full object-cover" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-bold text-gray-900 dark:text-white leading-tight flex items-start gap-1.5">
                <span className="material-icons text-gray-400 dark:text-gray-500 flex-shrink-0 mt-0.5" style={{ fontSize: '17px' }}>domain</span>
                <span className="line-clamp-2 break-words">{companyName || 'Eventos'}</span>
              </p>
            </div>
          </div>

          <div className="flex items-center gap-1.5 flex-shrink-0">
            <button
              type="button"
              onClick={toggleDarkMode}
              className="w-9 h-9 flex items-center justify-center rounded-full text-gray-500 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 active:scale-95 transition"
              title={isDarkMode ? 'Modo claro' : 'Modo escuro'}
            >
              <span className="material-icons" style={{ fontSize: '18px' }}>
                {isDarkMode ? 'light_mode' : 'dark_mode'}
              </span>
            </button>
            <button
              type="button"
              onClick={handleLogout}
              className="w-9 h-9 flex items-center justify-center rounded-full text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 active:scale-95 transition"
              title="Sair"
            >
              <span className="material-icons" style={{ fontSize: '18px' }}>logout</span>
            </button>
          </div>
      </div>

      <AutoRefreshTimer intervalSeconds={60} onRefresh={loadStats} isRefreshing={loading} />

      {/* Content */}
      <div className="relative flex-1 min-h-0">
        <div className="absolute inset-0 overflow-y-auto px-4 pt-4 pb-4">
        {!errorType && (
          <div className="flex items-center justify-between gap-3 mb-4">
            {username && !loading ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Bem-vindo, <span className="font-semibold text-gray-800 dark:text-gray-100 ml-1.5">{username}</span>
              </p>
            ) : <span />}
            <PeriodToggle period={period} onPeriodChange={setPeriod} />
          </div>
        )}

        {loading && (
          <div className={cardsContainerClass}>
            <CardSkeleton />
            <CardSkeleton />
            <CardSkeleton />
          </div>
        )}

        {!loading && errorType && (
          <ErrorState type={errorType} onRetry={loadStats} />
        )}

        {!loading && !errorType && stats.length === 0 && (
          <div className="flex flex-col items-center justify-center text-center py-16 px-6">
            <span className="material-icons text-gray-300 dark:text-gray-600 mb-3" style={{ fontSize: '44px' }}>
              bar_chart
            </span>
            <p className="text-sm font-semibold text-gray-700 dark:text-gray-200">Sem histórico de eventos</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Nenhum evento foi processado ainda para esta empresa {PERIOD_EMPTY_LABEL[period] || 'nos últimos 7 dias'}
            </p>
          </div>
        )}

        {!loading && !errorType && stats.length > 0 && (
          <>
            <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700/60 text-gray-600 dark:text-gray-300">
                  <span className="font-semibold text-gray-900 dark:text-white">{stats.length}</span>
                  {stats.length === 1 ? 'integração' : 'integrações'}
                </span>
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300">
                  <span className="font-semibold">{totalEvents}</span>
                  {totalEvents === 1 ? 'evento' : 'eventos'}
                </span>
                {totalProcessing > 0 && (
                  <span
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300"
                    title={`${totalProcessing} ${totalProcessing === 1 ? 'evento processando' : 'eventos processando'}`}
                  >
                    <span className="relative flex w-2 h-2">
                      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-blue-400 opacity-75" />
                      <span className="relative inline-flex w-2 h-2 rounded-full bg-blue-500" />
                    </span>
                    <span className="font-semibold">{totalProcessing}</span> processando
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <ChartTypeToggle chartType={chartType} onChartTypeChange={setChartType} />
                <LayoutModeToggle layoutMode={layoutMode} onLayoutModeChange={setLayoutMode} />
              </div>
            </div>

            <div className="flex items-center divide-x divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl px-3 py-2 mb-4">
              <div className="flex-1 flex flex-col items-center gap-0.5 px-1">
                <span className="flex items-center gap-1 text-sm font-bold text-gray-900 dark:text-white">
                  <span className="inline-block w-2 h-2 rounded-full bg-emerald-500" />
                  {totalSuccess}
                </span>
                <span className="text-[10px] text-gray-500 dark:text-gray-400">Sucesso</span>
              </div>
              <div className="flex-1 flex flex-col items-center gap-0.5 px-1">
                <span className="flex items-center gap-1 text-sm font-bold text-gray-900 dark:text-white">
                  <span className="inline-block w-2 h-2 rounded-full bg-red-500" />
                  {totalErrors}
                </span>
                <span className="text-[10px] text-gray-500 dark:text-gray-400">Falha</span>
              </div>
              <div className="flex-1 flex flex-col items-center gap-0.5 px-1">
                <span className="text-sm font-bold text-gray-900 dark:text-white">
                  {totalFinished > 0 ? `${successRate.toFixed(1)}%` : '—'}
                </span>
                <span className="text-[10px] text-gray-500 dark:text-gray-400">Taxa de sucesso</span>
              </div>
            </div>

            <div className={cardsContainerClass}>
              {stats.map((stat) => (
                <IntegrationEventCard
                  key={stat.integration_id}
                  stat={stat}
                  period={period}
                  chartType={chartType}
                  onOpenIncidences={handleOpenIncidences}
                />
              ))}
            </div>
          </>
        )}
        </div>

        {/* Refresh FAB */}
        <button
          type="button"
          onClick={loadStats}
          disabled={loading}
          title="Atualizar"
          className="absolute bottom-[max(1rem,calc(env(safe-area-inset-bottom)+0.5rem))] right-4 z-20 w-14 h-14 rounded-full flex items-center justify-center text-white shadow-lg active:scale-95 disabled:opacity-70 transition"
          style={{ backgroundColor: '#1B8A70', boxShadow: '0 8px 20px rgba(27,138,112,0.45)' }}
        >
          <span className={`material-icons ${loading ? 'animate-spin' : ''}`} style={{ fontSize: '24px' }}>
            refresh
          </span>
        </button>

        {/* Franchise Usage FAB + popover */}
        <button
          type="button"
          onClick={() => setUsageOpen((v) => !v)}
          title="Consumo da franquia"
          className={`absolute bottom-[max(1rem,calc(env(safe-area-inset-bottom)+0.5rem))] left-4 z-20 w-14 h-14 rounded-full flex items-center justify-center shadow-lg active:scale-95 transition ${
            usageOpen
              ? 'bg-gray-900 dark:bg-white text-white dark:text-gray-900'
              : 'bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-300 border border-gray-200 dark:border-gray-700'
          }`}
        >
          <span className="material-icons" style={{ fontSize: '22px' }}>data_usage</span>
        </button>

        {/* Tap-outside-to-close backdrop (transparent - this is a popover, not a modal) */}
        <div
          className={`fixed inset-0 z-10 transition-opacity duration-200 ${
            usageOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
          }`}
          onClick={() => setUsageOpen(false)}
        />

        <div
          className={`absolute bottom-[max(4.5rem,calc(env(safe-area-inset-bottom)+4rem))] left-4 z-30 w-72 max-w-[85vw] origin-bottom-left transition-all duration-200 ${
            usageOpen ? 'opacity-100 scale-100 pointer-events-auto' : 'opacity-0 scale-95 pointer-events-none'
          }`}
        >
          <div className="flex items-center justify-between mb-1.5 px-1">
            <p className="text-xs font-semibold text-gray-700 dark:text-gray-200 flex items-center gap-1.5">
              <span className="material-icons" style={{ fontSize: '14px' }}>data_usage</span>
              Consumo da Franquia
            </p>
            <button
              type="button"
              onClick={() => setUsageOpen(false)}
              className="w-6 h-6 flex items-center justify-center rounded-full text-gray-400 dark:text-gray-500 active:scale-95 transition"
            >
              <span className="material-icons" style={{ fontSize: '14px' }}>close</span>
            </button>
          </div>
          <div className="rounded-2xl shadow-xl overflow-hidden">
            <FranchiseUsageStrip apiToken={token} />
          </div>
        </div>
      </div>
    </div>
  )
}

export default DashboardPage
